import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    DatePicker,
    Empty,
    Input,
    Modal,
    Popconfirm,
    Popover,
    Select,
    Space,
    Spin,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload,
    message,
} from 'antd';
import {
    DeleteOutlined,
    DownloadOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    MailOutlined,
    PlusOutlined,
    ReloadOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../../hooks/useAuth';
import settlementApi from '../../../../api/settlementApi';
import { sendUniversalEmail } from '../../../../api/flightOperationEmailApi';
import GeneralEmailEditor from '../../../../components/EmailEditor/GeneralEmailEditor';
import { adaptUserEmailConfig } from '../../../../components/EmailEditor/components/EmailEditorForm';
import { exportDataToExcel } from '../../../../utils/excelUtils';
import AirportFuelPriceTrendChart from './AirportFuelPriceTrendChart';
import './AirportFuelPricePanel.css';

const { Text } = Typography;

const ALL_YEARS = '__ALL__';

// ---- 机场燃油价格变动通知邮件（规格：docs/fzx_doc/结算/机场燃油价格变动通知邮件/）----
// 默认收件人组：分组名常量，由后端按名称解析为邮箱；改收件人只需在"邮箱地址与分组"页面维护该组
const DEFAULT_NOTIFY_GROUP = '机场燃油价格通知组';
const NOTIFY_SUBJECT = '【FlyMeta】机场燃油价格已更新';
// 稳定引用：编辑器 businessContext 默认参数 {} 每次渲染都是新对象，会让其内部 effect 每轮重跑
const NOTIFY_EMPTY_CONTEXT = {};

const weekSeq = (label) => {
    const m = /W(\d+)/i.exec(label || '');
    return m ? Number(m[1]) : 99;
};

const formatPrice = (value) => {
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return '';
    // 油价多为整数：整数不补 .00，有小数才保留（最多 2 位），避免区间文本过长撑破列宽
    return Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
};

// 单元格展示：区间显示「下界–上界」，单值照旧
const formatPriceCell = (cell) => {
    if (!cell || cell.low === null || cell.low === undefined || cell.low === '') return '';
    const low = formatPrice(cell.low);
    if (cell.high === null || cell.high === undefined || cell.high === '') return low;
    return `${low}–${formatPrice(cell.high)}`;
};

// 导出用：区间写成 1400-1600 文本（原始数值，便于再次导入）
const formatExportCell = (cell) => {
    if (!cell || cell.low === null || cell.low === undefined || cell.low === '') return '';
    return cell.high === null || cell.high === undefined || cell.high === '' ? `${cell.low}` : `${cell.low}-${cell.high}`;
};

// 报价期按月展示
const monthLabel = (value) => (value ? String(value).slice(0, 7) : '');
// 适用报价日期精确到日
const dateLabel = (value) => (value ? String(value).slice(0, 10) : '');

const cellHasNote = (cell) => !!(cell && (cell.effective_start || cell.effective_end || cell.remark));

// 把单元格里的价格文本解析成 { low, high } / { empty } / { invalid }
const parsePriceText = (text) => {
    const t = String(text ?? '').trim().replace(/[，,]/g, '');
    if (!t) return { empty: true };
    const m = t.match(/^(\d+(?:\.\d+)?)\s*[-~—]\s*(\d+(?:\.\d+)?)$/);
    if (m) return { low: Number(m[1]), high: Number(m[2]) };
    const n = Number(t);
    if (Number.isFinite(n)) return { low: n, high: null };
    return { invalid: true };
};

const noteTooltipNode = (cell) => {
    const lines = [];
    if (cell.effective_start || cell.effective_end) {
        lines.push(`适用：${dateLabel(cell.effective_start) || '…'} ~ ${dateLabel(cell.effective_end) || '…'}`);
    }
    if (cell.remark) lines.push(`备注：${cell.remark}`);
    return <div style={{ whiteSpace: 'pre-line', maxWidth: 260, fontSize: 12 }}>{lines.join('\n')}</div>;
};

/**
 * 单元格编辑器（Popover 内容）：价格/区间 + 适用报价日期(到日) + 备注。
 * 本地维护表单态，避免逐键触发整表重渲染。
 */
const PriceCellEditor = ({ cell, saving, onSave, onCancel }) => {
    const initialPrice =
        cell.low === null || cell.low === undefined
            ? ''
            : cell.high === null || cell.high === undefined
                ? `${cell.low}`
                : `${cell.low}-${cell.high}`;
    const [priceText, setPriceText] = useState(initialPrice);
    const [effStart, setEffStart] = useState(cell.effective_start ? dayjs(cell.effective_start) : null);
    const [effEnd, setEffEnd] = useState(cell.effective_end ? dayjs(cell.effective_end) : null);
    const [remark, setRemark] = useState(cell.remark || '');

    const submit = () =>
        onSave({
            priceText,
            effStart: effStart ? effStart.format('YYYY-MM-DD') : null,
            effEnd: effEnd ? effEnd.format('YYYY-MM-DD') : null,
            remark,
        });

    return (
        <div style={{ width: 248 }}>
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>价格 / 区间（USD/吨）</Text>
                    <Input
                        autoFocus
                        size="small"
                        value={priceText}
                        placeholder="1500 或 1400-1600"
                        onChange={(e) => setPriceText(e.target.value)}
                        onPressEnter={submit}
                    />
                </div>
                <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>适用报价日期</Text>
                    <Space size={4} align="center">
                        <DatePicker size="small" value={effStart} format="YYYY-MM-DD" placeholder="起" onChange={setEffStart} style={{ width: 116 }} />
                        <span style={{ color: '#bfbfbf' }}>~</span>
                        <DatePicker size="small" value={effEnd} format="YYYY-MM-DD" placeholder="止" onChange={setEffEnd} style={{ width: 116 }} />
                    </Space>
                </div>
                <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>备注</Text>
                    <Input.TextArea size="small" value={remark} rows={2} maxLength={500} onChange={(e) => setRemark(e.target.value)} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>清空价格保存可删除</Text>
                    <Space size={6}>
                        <Button size="small" onClick={onCancel}>取消</Button>
                        <Button size="small" type="primary" loading={saving} onClick={submit}>保存</Button>
                    </Space>
                </div>
            </Space>
        </div>
    );
};

/**
 * 机场燃油价格 Tab（矩阵视图）
 * - 行：报价期（精确到月）+ 周次（默认最新在上）
 * - 列：各机场（已停飞机场默认收起，可用开关调出）
 * - 单元格点选用 Popover 编辑：价格/区间 + 适用报价日期 + 备注（需 edit 权限并开启「编辑」开关）
 * - 带适用区间或备注的单元格右上角有角标，鼠标悬停弹出批注浮层
 */
const AirportFuelPricePanel = ({ active, canEdit = false }) => {
    const { user, hasPermission, userEmailConfig } = useAuth();
    const canSendMail = hasPermission('mail:send');

    const [items, setItems] = useState([]);
    const [serverAirports, setServerAirports] = useState([]); // [{ code, is_active }]
    const [unit, setUnit] = useState('USD/T');
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const [editMode, setEditMode] = useState(false);
    const [yearFilter, setYearFilter] = useState(ALL_YEARS);
    const [trendGranularity, setTrendGranularity] = useState('auto');
    const [showInactiveAirports, setShowInactiveAirports] = useState(false);

    // 客户端临时新增的空行/空列（尚无数据时也能展示以便填写）
    const [extraRows, setExtraRows] = useState([]); // [{period_date, week_label}]
    const [extraAirports, setExtraAirports] = useState([]); // [code]

    // 单元格编辑态：{ rowKey, airport }
    const [editing, setEditing] = useState(null);
    const [savingKey, setSavingKey] = useState(null);

    // 新增周期 / 新增机场弹窗
    const [periodModalOpen, setPeriodModalOpen] = useState(false);
    const [newPeriodMonth, setNewPeriodMonth] = useState(null);
    const [newWeekLabel, setNewWeekLabel] = useState('');
    const [airportModalOpen, setAirportModalOpen] = useState(false);
    const [newAirport, setNewAirport] = useState('');
    const [importing, setImporting] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [previewData, setPreviewData] = useState(null); // 导入预览结果（含覆盖明细）
    const [pendingImportFile, setPendingImportFile] = useState(null); // 待确认导入的文件

    // 「完成并通知」发件界面状态
    const [notifyOpen, setNotifyOpen] = useState(false);
    const [notifyBodyHtml, setNotifyBodyHtml] = useState(''); // 打开时快照正文，操作人/时间不随重渲染漂移
    const [notifySending, setNotifySending] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await settlementApi.airportFuelPrice.list();
            const payload = response?.data || {};
            setItems(payload.items || []);
            setServerAirports(payload.airports || []);
            setUnit(payload.unit || 'USD/T');
            setLoaded(true);
        } catch (error) {
            console.error('加载机场燃油价格失败:', error);
            message.error('加载机场燃油价格失败');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (active && !loaded) loadData();
    }, [active, loaded, loadData]);

    // 退出编辑模式时清空进行中的单元格编辑
    useEffect(() => {
        if (!editMode) setEditing(null);
    }, [editMode]);

    // ---- 机场列（含停飞状态、收起过滤）----
    const airportMeta = useMemo(() => {
        const m = new Map();
        serverAirports.forEach((a) => m.set(a.code, a.is_active !== false));
        return m;
    }, [serverAirports]);

    const inactiveCount = useMemo(
        () => serverAirports.filter((a) => a.is_active === false).length,
        [serverAirports],
    );

    const airports = useMemo(() => {
        const ordered = serverAirports.map((a) => a.code);
        extraAirports.forEach((code) => {
            if (!ordered.includes(code)) ordered.push(code);
        });
        // 已停飞机场默认收起（未登记/新增列视为在飞）
        return ordered.filter((code) => showInactiveAirports || airportMeta.get(code) !== false);
    }, [serverAirports, extraAirports, showInactiveAirports, airportMeta]);

    const years = useMemo(() => {
        const set = new Set();
        items.forEach((it) => {
            if (it.period_date) set.add(it.period_date.slice(0, 4));
        });
        return Array.from(set).sort((a, b) => b.localeCompare(a));
    }, [items]);

    const matrixRows = useMemo(() => {
        const rowMap = new Map();
        const ensureRow = (period_date, week_label) => {
            const key = `${period_date}__${week_label}`;
            if (!rowMap.has(key)) {
                rowMap.set(key, { key, period_date, week_label, cells: {} });
            }
            return rowMap.get(key);
        };
        items.forEach((it) => {
            const row = ensureRow(it.period_date, it.week_label);
            row.cells[it.airport_code] = {
                id: it.id,
                low: it.price_usd_per_t,
                high: it.price_usd_per_t_high ?? null,
                effective_start: it.effective_start ?? null,
                effective_end: it.effective_end ?? null,
                remark: it.remark ?? null,
            };
        });
        extraRows.forEach((r) => ensureRow(r.period_date, r.week_label));

        let rows = Array.from(rowMap.values());
        if (yearFilter !== ALL_YEARS) {
            rows = rows.filter((r) => (r.period_date || '').slice(0, 4) === yearFilter);
        }
        rows.sort((a, b) => {
            const cmp = (b.period_date || '').localeCompare(a.period_date || '');
            if (cmp !== 0) return cmp;
            return weekSeq(b.week_label) - weekSeq(a.week_label);
        });

        // 标注同报价期分组的边界，供「框住」描边使用。
        return rows.map((r, i) => {
            const prev = rows[i - 1];
            const next = rows[i + 1];
            return {
                ...r,
                isPeriodStart: !prev || prev.period_date !== r.period_date,
                isPeriodEnd: !next || next.period_date !== r.period_date,
            };
        });
    }, [items, extraRows, yearFilter]);

    // 图表数据跟随年份筛选，与表格保持一致
    const chartItems = useMemo(() => {
        if (yearFilter === ALL_YEARS) return items;
        return items.filter((it) => (it.period_date || '').slice(0, 4) === yearFilter);
    }, [items, yearFilter]);

    const rowClassName = useCallback((record) => {
        const cls = [];
        if (record.isPeriodStart) cls.push('afp-period-start');
        if (record.isPeriodEnd) cls.push('afp-period-end');
        return cls.join(' ');
    }, []);

    // ---- 单元格保存（immutable 更新 items）----
    const applyUpsert = (record) => {
        setItems((prev) => {
            const idx = prev.findIndex(
                (it) =>
                    it.airport_code === record.airport_code &&
                    it.period_date === record.period_date &&
                    it.week_label === record.week_label,
            );
            if (idx === -1) return [...prev, record];
            const next = [...prev];
            next[idx] = record;
            return next;
        });
    };

    const removeFromItems = (predicate) => {
        setItems((prev) => prev.filter((it) => !predicate(it)));
    };

    // 仅当编辑焦点仍停在本格时才退出编辑态（保存为异步，期间用户可能已点开另一格）
    const clearEditingIfCurrent = (rowKey, airport) => {
        setEditing((cur) => (cur && cur.rowKey === rowKey && cur.airport === airport ? null : cur));
    };

    const saveCell = async (row, airport, values) => {
        const cellKey = `${row.key}__${airport}`;
        const cell = row.cells[airport] || {};
        const existingId = cell.id;
        const parsed = parsePriceText(values.priceText);

        // 清空价格 → 删除该单元格（含备注/适用区间）
        if (parsed.empty) {
            if (!existingId) {
                clearEditingIfCurrent(row.key, airport);
                return;
            }
            setSavingKey(cellKey);
            try {
                await settlementApi.airportFuelPrice.remove(existingId);
                removeFromItems((it) => it.id === existingId);
                message.success('已清除');
            } catch (error) {
                console.error('清除价格失败:', error);
                message.error(error?.response?.data?.detail || '清除失败');
            } finally {
                setSavingKey(null);
                clearEditingIfCurrent(row.key, airport);
            }
            return;
        }

        if (parsed.invalid) {
            message.error('价格格式不正确（示例：1500 或 1400-1600）');
            return;
        }
        if (parsed.low < 0 || (parsed.high !== null && parsed.high < 0)) {
            message.error('价格不能为负');
            return;
        }
        if (parsed.high !== null && parsed.high < parsed.low) {
            message.error('价格区间上界不能小于下界');
            return;
        }
        const effStart = values.effStart || null;
        const effEnd = values.effEnd || null;
        if (effStart && effEnd && effEnd < effStart) {
            message.error('适用区间的结束不能早于开始');
            return;
        }

        setSavingKey(cellKey);
        try {
            const response = await settlementApi.airportFuelPrice.upsert({
                airport_code: airport,
                period_date: row.period_date,
                week_label: row.week_label,
                price_usd_per_t: parsed.low,
                price_usd_per_t_high: parsed.high,
                effective_start: effStart,
                effective_end: effEnd,
                remark: values.remark ? values.remark.trim() : null,
            });
            const record = response?.data?.record;
            if (record) applyUpsert(record);
            message.success('已保存');
        } catch (error) {
            console.error('保存价格失败:', error);
            message.error(error?.response?.data?.detail || '保存失败');
        } finally {
            setSavingKey(null);
            clearEditingIfCurrent(row.key, airport);
        }
    };

    const handleDeleteRow = async (row) => {
        try {
            await settlementApi.airportFuelPrice.deletePeriod({
                period_date: row.period_date,
                week_label: row.week_label,
            });
            removeFromItems((it) => it.period_date === row.period_date && it.week_label === row.week_label);
            setExtraRows((prev) => prev.filter((r) => !(r.period_date === row.period_date && r.week_label === row.week_label)));
            message.success('已删除该周次');
        } catch (error) {
            console.error('删除周次失败:', error);
            message.error(error?.response?.data?.detail || '删除失败');
        }
    };

    const handleAddPeriod = () => {
        if (!newPeriodMonth || !newWeekLabel.trim()) {
            message.warning('请选择报价期月份并填写周次标签');
            return;
        }
        const period_date = newPeriodMonth.startOf('month').format('YYYY-MM-DD');
        const week_label = newWeekLabel.trim();
        const exists =
            items.some((it) => it.period_date === period_date && it.week_label === week_label) ||
            extraRows.some((r) => r.period_date === period_date && r.week_label === week_label);
        if (exists) {
            message.warning('该报价期+周次已存在');
            return;
        }
        setExtraRows((prev) => [...prev, { period_date, week_label }]);
        if (yearFilter !== ALL_YEARS && period_date.slice(0, 4) !== yearFilter) {
            setYearFilter(ALL_YEARS);
        }
        setPeriodModalOpen(false);
        setNewPeriodMonth(null);
        setNewWeekLabel('');
        message.success('已新增空白周次，请点选单元格填写价格');
    };

    const handleAddAirport = () => {
        const code = newAirport.trim();
        if (!code) {
            message.warning('请填写机场代码');
            return;
        }
        // 按全集查重：已停飞机场被收起时不在 airports 里，需对 serverAirports 一并判重
        const existsInServer = serverAirports.some((a) => a.code === code);
        if (existsInServer || airports.includes(code)) {
            message.warning(
                existsInServer && !airports.includes(code)
                    ? '该机场已存在且当前为停飞状态，请打开「显示已停飞机场」查看'
                    : '该机场列已存在',
            );
            return;
        }
        setExtraAirports((prev) => [...prev, code]);
        setAirportModalOpen(false);
        setNewAirport('');
        message.success(`已新增机场列 ${code}，请点选单元格填写价格`);
    };

    const toggleAirportStatus = async (code, currentlyActive) => {
        try {
            const resp = await settlementApi.airportFuelPrice.setAirportStatus({
                airport_code: code,
                is_active: !currentlyActive,
            });
            const next = resp?.data || {};
            const nextActive = next.is_active !== false;
            setServerAirports((prev) => {
                const found = prev.some((a) => a.code === code);
                if (found) return prev.map((a) => (a.code === code ? { ...a, is_active: nextActive } : a));
                return [...prev, { code, is_active: nextActive }];
            });
            message.success(nextActive ? `已恢复 ${code}` : `已标记 ${code} 停飞`);
        } catch (error) {
            console.error('标记机场停飞状态失败:', error);
            message.error(error?.response?.data?.detail || '操作失败');
        }
    };

    // 选择文件后先干跑预览（不写库），把"将新增 / 将覆盖"明细交给用户二次确认
    const handleImport = async (file) => {
        setPreviewing(true);
        try {
            const response = await settlementApi.airportFuelPrice.previewImportExcel(file);
            setPreviewData(response?.data || {});
            setPendingImportFile(file);
        } catch (error) {
            console.error('导入预览失败:', error);
            message.error(error?.response?.data?.detail || '导入预览失败');
        } finally {
            setPreviewing(false);
        }
        return false; // 阻止 Upload 默认上传行为
    };

    // 用户在预览弹窗点"确认导入"后才真正写库
    const confirmImport = async () => {
        if (!pendingImportFile) return;
        setImporting(true);
        try {
            const response = await settlementApi.airportFuelPrice.importExcel(pendingImportFile);
            const result = response?.data || {};
            message.success(`导入成功：新增 ${result.created || 0} 条，更新 ${result.updated || 0} 条`);
            if (result.errors?.length > 0) {
                message.warning(`${result.errors.length} 条提示：${result.errors.slice(0, 2).join('；')}`);
            }
            setExtraRows([]);
            setPreviewData(null);
            setPendingImportFile(null);
            await loadData();
        } catch (error) {
            console.error('导入失败:', error);
            message.error(error?.response?.data?.detail || '导入失败');
        } finally {
            setImporting(false);
        }
    };

    const cancelImport = () => {
        if (importing) return;
        setPreviewData(null);
        setPendingImportFile(null);
    };

    const handleExport = () => {
        if (matrixRows.length === 0) {
            message.warning('暂无数据可导出');
            return;
        }
        const rows = matrixRows.map((row) => {
            const obj = { 报价期: monthLabel(row.period_date), 周次: row.week_label };
            airports.forEach((a) => {
                obj[a] = formatExportCell(row.cells[a]);
            });
            return obj;
        });
        exportDataToExcel(rows, '机场油价', `各地机场油价_${dayjs().format('YYYYMMDD')}.xlsx`);
        message.success('导出成功');
    };

    // ---- 「完成并通知」：发出"油价已更新"通知邮件（与保存解耦，不触发额外保存）----
    // 直达链接：前端运行时 origin 是唯一来源，T02 已让 ?tab= 能直达本 TAB
    const buildNotifyBodyHtml = () => {
        const link = `${window.location.origin}/settlement/statistics-dashboard?tab=airport_fuel_price`;
        const operator = user?.username || '';
        const time = dayjs().format('YYYY-MM-DD HH:mm');
        return [
            '<p>机场燃油价格已更新，请登录系统查看：</p>',
            `<p><a href="${link}">机场燃油价格</a></p>`,
            `<p>操作人：${operator}　时间：${time}</p>`,
        ].join('');
    };

    // 发件人固定用操作人自己绑定的邮箱（user_email_config），不再走公共预设账号
    const openNotify = () => {
        if (!userEmailConfig) {
            message.warning('请先在「邮箱配置」中绑定个人邮箱，再发送通知');
            return;
        }
        setNotifyBodyHtml(buildNotifyBodyHtml());
        setNotifyOpen(true);
    };

    const handleNotifySubmit = async (emailPayload) => {
        const recipients = [];
        (emailPayload.to || []).forEach((item) => {
            if (item?.trim()) recipients.push({ email: item.trim(), type: 'TO' });
        });
        (emailPayload.cc || []).forEach((item) => {
            if (item?.trim()) recipients.push({ email: item.trim(), type: 'CC' });
        });
        if (recipients.length === 0) {
            message.error('请至少选择一个收件人');
            return;
        }
        // GeneralEmailEditor 不校验空正文，在此拦截
        const bodyText = (emailPayload.contentHtml || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        if (!bodyText) {
            message.error('请填写正文');
            return;
        }

        setNotifySending(true);
        try {
            const response = await sendUniversalEmail({
                recipients,
                subject: emailPayload.subject || NOTIFY_SUBJECT,
                body_html: emailPayload.contentHtml,
                // 契约必填占位，后端会以当前登录用户强制覆盖（规格「数据结构」节）
                sender_user_id: user?.id || 1,
                // 一封邮件带多个收件人，整封原子成败
                is_bulk_split: false,
                // 不传预设账号 → 后端回退到操作人绑定的个人邮箱（user_email_config）发送
                preset_sender_key: null,
            });
            if (response?.success) {
                message.success('通知邮件已发送，可在发件箱查看');
                setNotifyOpen(false);
            } else {
                message.error(response?.message || '通知邮件发送失败');
            }
        } catch (error) {
            console.error('发送机场燃油价格通知失败:', error);
            message.error(error?.response?.data?.detail || '通知邮件发送失败');
        } finally {
            setNotifySending(false);
        }
    };

    // ---- 列定义 ----
    const renderPriceCell = (row, airport) => {
        const cell = row.cells[airport] || {};
        const cellKey = `${row.key}__${airport}`;
        const isEditing = editing && editing.rowKey === row.key && editing.airport === airport;
        const isSaving = savingKey === cellKey;
        const hasNote = cellHasNote(cell);
        const editable = canEdit && editMode;
        const empty = cell.low === null || cell.low === undefined;

        const inner = (
            <div
                className={`afp-price-cell${hasNote ? ' afp-has-note' : ''}`}
                onClick={editable && !isSaving ? () => setEditing({ rowKey: row.key, airport }) : undefined}
                style={{
                    position: 'relative',
                    minHeight: 22,
                    cursor: editable ? 'pointer' : 'default',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    padding: '0 4px',
                    borderRadius: 3,
                    background: editable ? '#fafafa' : 'transparent',
                    color: empty ? '#bfbfbf' : '#1f1f1f',
                }}
            >
                {isSaving ? <Spin size="small" /> : formatPriceCell(cell) || (editable ? '—' : '')}
                {hasNote && <span className="afp-note-marker" />}
            </div>
        );

        if (editable) {
            return (
                <Popover
                    open={!!isEditing}
                    trigger="click"
                    placement="bottom"
                    destroyTooltipOnHide
                    onOpenChange={(open) => {
                        if (!open) clearEditingIfCurrent(row.key, airport);
                    }}
                    content={
                        isEditing ? (
                            <PriceCellEditor
                                cell={cell}
                                saving={isSaving}
                                onSave={(vals) => saveCell(row, airport, vals)}
                                onCancel={() => clearEditingIfCurrent(row.key, airport)}
                            />
                        ) : null
                    }
                >
                    {inner}
                </Popover>
            );
        }

        return hasNote ? <Tooltip title={noteTooltipNode(cell)}>{inner}</Tooltip> : inner;
    };

    const renderAirportHeader = (code) => {
        const isActive = airportMeta.get(code) !== false;
        const codeNode = (
            <span style={{ color: isActive ? undefined : '#bfbfbf' }}>
                {code}
                {!isActive && <Tag style={{ marginLeft: 4, transform: 'scale(0.85)' }}>停飞</Tag>}
            </span>
        );
        if (!(canEdit && editMode)) return codeNode;
        return (
            <Space size={0} align="center">
                {codeNode}
                <Popconfirm
                    title={isActive ? `标记 ${code} 为已停飞？将默认收起该列` : `恢复 ${code} 运营？`}
                    okText="确定"
                    cancelText="取消"
                    onConfirm={() => toggleAirportStatus(code, isActive)}
                >
                    <Tooltip title={isActive ? '标记停飞' : '恢复运营'}>
                        <Button type="text" size="small" style={{ padding: '0 2px' }} icon={isActive ? <EyeInvisibleOutlined /> : <EyeOutlined />} />
                    </Tooltip>
                </Popconfirm>
            </Space>
        );
    };

    // 列定义每次渲染重建：内部 render 闭包引用 editing/savingKey/airportMeta 等可变态，
    // 列数有限（固定列 + 机场列），开销可忽略，避免 useMemo 漏依赖导致的 stale closure。
    const buildColumns = () => {
        const base = [
            {
                title: '报价期',
                dataIndex: 'period_date',
                key: 'period_date',
                width: 96,
                fixed: 'left',
                align: 'center',
                render: (text, row) =>
                    row.isPeriodStart
                        ? <Text strong style={{ fontSize: 12, color: '#1890ff' }}>{monthLabel(text)}</Text>
                        : <Text style={{ fontSize: 12, color: '#bfbfbf' }}>{monthLabel(text)}</Text>,
            },
            {
                title: '周次',
                dataIndex: 'week_label',
                key: 'week_label',
                width: 110,
                fixed: 'left',
                align: 'center',
                render: (text) => <Tag color="geekblue" style={{ margin: 0 }}>{text}</Tag>,
            },
        ];

        const airportCols = airports.map((a) => ({
            title: renderAirportHeader(a),
            key: a,
            width: 116,
            align: 'right',
            render: (_, row) => renderPriceCell(row, a),
        }));

        const tail = [];
        if (canEdit && editMode) {
            tail.push({
                title: '操作',
                key: '__action',
                width: 70,
                fixed: 'right',
                align: 'center',
                render: (_, row) => (
                    <Popconfirm title="删除该周次全部机场价格？" okText="删除" cancelText="取消" onConfirm={() => handleDeleteRow(row)}>
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                ),
            });
        }
        return [...base, ...airportCols, ...tail];
    };
    const columns = buildColumns();

    return (
        <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
                <Space size={8} wrap>
                    <Text strong style={{ fontSize: 14 }}>⛽ 各地机场周度参考油价</Text>
                    <Tag color="blue">单位：{unit}</Tag>
                    <Tag>共 {matrixRows.length} 个周次</Tag>
                    <Select
                        size="small"
                        value={yearFilter}
                        onChange={setYearFilter}
                        style={{ width: 110 }}
                        options={[{ value: ALL_YEARS, label: '全部年份' }, ...years.map((y) => ({ value: y, label: y }))]}
                    />
                    {inactiveCount > 0 && (
                        <Tooltip title="已停飞机场默认收起，打开可临时查看其历史油价">
                            <Space size={6}>
                                <Switch size="small" checked={showInactiveAirports} onChange={setShowInactiveAirports} />
                                <Text style={{ fontSize: 12 }}>显示已停飞机场（{inactiveCount}）</Text>
                            </Space>
                        </Tooltip>
                    )}
                </Space>
                <Space size={8} wrap>
                    {canEdit && (
                        <Tooltip title="开启后可点选单元格编辑价格、区间、适用报价日期与备注">
                            <Space size={6}>
                                <Switch size="small" checked={editMode} onChange={setEditMode} />
                                <Text>编辑模式</Text>
                            </Space>
                        </Tooltip>
                    )}
                    {canEdit && editMode && (
                        <>
                            <Button size="small" icon={<PlusOutlined />} onClick={() => setPeriodModalOpen(true)}>新增周期</Button>
                            <Button size="small" icon={<PlusOutlined />} onClick={() => setAirportModalOpen(true)}>新增机场</Button>
                            <Upload accept=".xlsx,.xlsm" showUploadList={false} beforeUpload={handleImport}>
                                <Button size="small" type="primary" icon={<UploadOutlined />} loading={previewing}>导入Excel</Button>
                            </Upload>
                            <Tooltip title={canSendMail ? '发出"油价已更新"通知邮件（与保存互相独立）' : '无发送邮件权限'}>
                                {/* 禁用按钮不触发鼠标事件，需要 span 包一层让 Tooltip 仍能弹出 */}
                                <span>
                                    <Button
                                        size="small"
                                        type="primary"
                                        icon={<MailOutlined />}
                                        disabled={!canSendMail}
                                        onClick={openNotify}
                                    >
                                        完成并通知
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    )}
                    <Button size="small" icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
                    <Button size="small" icon={<ReloadOutlined />} onClick={loadData} loading={loading}>刷新</Button>
                </Space>
            </div>

            {canEdit && editMode && (
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12, padding: '8px 12px', background: '#e6f7ff', borderLeft: '3px solid #1890ff', borderRadius: 4 }}>
                    💡 编辑模式：点击单元格可填价格或区间（如 1400-1600）、适用报价日期与备注，回车或点保存即写入；清空价格保存将删除该单元格。点机场列表头的眼睛图标可标记停飞 / 恢复。导入 Excel 支持《各地机场油价汇总》宽表（价格单元格支持 1400-1600 区间写法；单元格的 Excel 批注会作为"备注"导入，同键覆盖时一并覆盖）；导入前会弹预览供确认。
                </div>
            )}

            <Spin spinning={loading}>
                <div className="afp-matrix-wrapper">
                    <Table
                        rowKey="key"
                        dataSource={matrixRows}
                        columns={columns}
                        rowClassName={rowClassName}
                        size="small"
                        bordered
                        scroll={{ x: 'max-content', y: 560 }}
                        pagination={false}
                        locale={{ emptyText: <Empty description="暂无机场油价数据，可导入 Excel 或手动新增" /> }}
                    />
                </div>
            </Spin>

            <AirportFuelPriceTrendChart
                items={chartItems}
                airports={airports}
                unit={unit}
                granularityMode={trendGranularity}
                onGranularityModeChange={setTrendGranularity}
            />

            <Modal
                title="新增报价周期"
                open={periodModalOpen}
                onOk={handleAddPeriod}
                onCancel={() => setPeriodModalOpen(false)}
                okText="新增"
                cancelText="取消"
                destroyOnClose
            >
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                    <div>
                        <Text type="secondary">报价期月份：</Text>
                        <DatePicker picker="month" value={newPeriodMonth} onChange={setNewPeriodMonth} format="YYYY-MM" style={{ width: '100%' }} />
                    </div>
                    <div>
                        <Text type="secondary">周次标签（如 W1_Jun26）：</Text>
                        <Input value={newWeekLabel} onChange={(e) => setNewWeekLabel(e.target.value)} placeholder="W1_Jun26" maxLength={20} />
                    </div>
                </Space>
            </Modal>

            <Modal
                title="新增机场列"
                open={airportModalOpen}
                onOk={handleAddAirport}
                onCancel={() => setAirportModalOpen(false)}
                okText="新增"
                cancelText="取消"
                destroyOnClose
            >
                <Text type="secondary">机场代码（IATA 三字码，如 PEK）：</Text>
                <Input value={newAirport} onChange={(e) => setNewAirport(e.target.value)} placeholder="PEK" maxLength={10} />
            </Modal>

            <Modal
                title="导入预览 · 请确认"
                open={!!previewData}
                onCancel={cancelImport}
                width={880}
                destroyOnClose
                maskClosable={!importing}
                footer={[
                    <Button key="cancel" onClick={cancelImport} disabled={importing}>取消</Button>,
                    <Button
                        key="ok"
                        type="primary"
                        loading={importing}
                        disabled={!previewData || (!previewData.created_count && !previewData.updated_count)}
                        onClick={confirmImport}
                    >
                        {previewData
                            ? `确认导入（新增 ${previewData.created_count || 0}、覆盖 ${previewData.updated_count || 0}）`
                            : '确认导入'}
                    </Button>,
                ]}
            >
                {previewData && (
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                        <Space size={12} wrap>
                            <Tag color="green">将新增 {previewData.created_count || 0} 条</Tag>
                            <Tag color="orange">将覆盖 {previewData.updated_count || 0} 条</Tag>
                            {previewData.skipped > 0 && <Tag color="red">跳过 {previewData.skipped} 条</Tag>}
                            {previewData.note_overwrites_count > 0 && (
                                <Tag color="volcano">含 {previewData.note_overwrites_count} 条备注将被覆盖</Tag>
                            )}
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                涉及机场 {previewData.airports?.length || 0} 个 · 报价期×周次 {previewData.periods || 0} 组
                            </Text>
                        </Space>

                        {previewData.updated_count > 0 && (
                            <div>
                                <Text strong style={{ fontSize: 13 }}>将被覆盖的已有记录（原价 → 新价；带 Excel 批注的同时覆盖备注）</Text>
                                <Table
                                    size="small"
                                    bordered
                                    rowKey={(r) => `${r.airport_code}__${r.period_date}__${r.week_label}`}
                                    dataSource={previewData.overwrites || []}
                                    pagination={false}
                                    scroll={{ y: 240 }}
                                    style={{ marginTop: 6 }}
                                    columns={[
                                        { title: '机场', dataIndex: 'airport_code', width: 80 },
                                        { title: '报价期', dataIndex: 'period_date', width: 84, render: (t) => monthLabel(t) },
                                        { title: '周次', dataIndex: 'week_label', width: 116 },
                                        {
                                            title: '原价',
                                            key: 'old',
                                            align: 'right',
                                            width: 92,
                                            render: (_, r) => <Text type="secondary" delete>{formatPriceCell(r.old)}</Text>,
                                        },
                                        {
                                            title: '新价',
                                            key: 'new',
                                            align: 'right',
                                            width: 92,
                                            render: (_, r) => <Text strong style={{ color: '#fa8c16' }}>{formatPriceCell(r.new)}</Text>,
                                        },
                                        {
                                            title: '备注变更',
                                            key: 'remark',
                                            render: (_, r) => {
                                                const newR = r.new?.remark;
                                                if (newR == null) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
                                                const oldR = r.old?.remark;
                                                return (
                                                    <Tooltip
                                                        title={
                                                            <div style={{ whiteSpace: 'pre-line', maxWidth: 320 }}>
                                                                {oldR ? `原：${oldR}\n新：${newR}` : `新增备注：${newR}`}
                                                            </div>
                                                        }
                                                    >
                                                        <div style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                                                            {oldR ? <Text type="secondary" delete>{oldR}</Text> : null}
                                                            {oldR ? ' → ' : ''}
                                                            <Text style={{ color: '#fa8c16' }}>{newR}</Text>
                                                        </div>
                                                    </Tooltip>
                                                );
                                            },
                                        },
                                    ]}
                                />
                                {previewData.overwrites_truncated > 0 && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        仅显示前 {previewData.overwrites?.length || 0} 条，另有 {previewData.overwrites_truncated} 条同样会被覆盖。
                                    </Text>
                                )}
                            </div>
                        )}

                        {previewData.errors?.length > 0 && (
                            <div>
                                <Text strong style={{ fontSize: 13, color: '#cf1322' }}>跳过 / 提示（{previewData.errors.length}）</Text>
                                <div style={{ maxHeight: 120, overflow: 'auto', fontSize: 12, color: '#8c8c8c', marginTop: 6, whiteSpace: 'pre-line' }}>
                                    {previewData.errors.join('\n')}
                                </div>
                            </div>
                        )}

                        {!previewData.created_count && !previewData.updated_count && (
                            <Text type="warning">没有可写入的有效数据，请检查文件内容。</Text>
                        )}
                    </Space>
                )}
            </Modal>

            {/* 「完成并通知」发件界面：复用通用邮件编辑器（隐藏内容块侧栏），主题/正文/默认收件人组预填 */}
            {notifyOpen && (
                <GeneralEmailEditor
                    visible={notifyOpen}
                    title="机场燃油价格变动通知"
                    showSidePanel={false}
                    initialSubject={NOTIFY_SUBJECT}
                    templateContent={notifyBodyHtml}
                    businessContext={NOTIFY_EMPTY_CONTEXT}
                    to={DEFAULT_NOTIFY_GROUP}
                    defaultSender={adaptUserEmailConfig(userEmailConfig)}
                    senderEditable={false}
                    loading={notifySending}
                    onSubmit={handleNotifySubmit}
                    onCancel={() => setNotifyOpen(false)}
                />
            )}
        </div>
    );
};

export default AirportFuelPricePanel;
